/** /composition_proposals/ — the review surface of the Figma/HTML design
 * import (Figma plan §7).
 *
 * A proposal is a tenant-scoped, dependency-ordered bundle of typed entity
 * drafts (notification / delivery_method / design / block / theme / …) plus
 * §12.5 questions. Review decisions live here; the launchers (discover /
 * start_ingest / start_apply) enqueue the `composition_import` background
 * task, polled via /bgtasks/{id}/ like every other launcher.
 *
 * Load-bearing contract points mirrored from the backend:
 * - `required_permission` gates answering (e.g. console_tag_taxonomy_approve);
 *   `can_answer` is precomputed for the current user.
 * - Discussion SUPERSEDES a question (new row via `superseded_by`) — the
 *   original is provenance and no longer gates apply.
 * - Items of reserved kinds (`appliable: false`, e.g. integration_config)
 *   refuse acceptance — they are review information, not appliable drafts.
 * - start_apply 409s while blocking questions are open, nothing is accepted,
 *   the proposal is not in review, or a task is already running.
 * - Apply is ALL-OR-NOTHING (the change engine, AIE §2): a failed apply wrote
 *   nothing — the failing item carries `evidence.apply_error`, the others stay
 *   accepted. `return_to_review` puts a failed proposal back in review (its
 *   failed items become proposed: fix, re-accept, apply again).
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules).
 */
import { apiGet, apiPost } from './client';
import type { MaterialTenant, UserBrief } from './materials';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

export type ProposalStatus = 'draft' | 'review' | 'applying' | 'applied' | 'rejected' | 'failed';
/** `mcp` — proposed through the MCP endpoint; `chat` — the console agent. */
export type ProposalOrigin = 'figma' | 'html' | 'intake' | 'chat' | 'mcp' | 'translation_sync';
export type ItemStatus = 'proposed' | 'accepted' | 'rejected' | 'applied' | 'failed';
/** `noop` = reuse an existing row (writes nothing); `delete` — the change
 *  engine's v1 kinds (blocks, block lists, rulesets, language policies…). */
export type ItemAction = 'create' | 'update' | 'delete' | 'noop';

export interface ProposalListItem {
  id: number;
  tour_operator: MaterialTenant;
  status: ProposalStatus;
  origin: ProposalOrigin;
  change_set_id: string;
  item_count: number | null;
  open_blocking_questions: number | number[] | null;
  created_by: UserBrief | null;
  created_ts: string | null;
  updated_ts: string | null;
}

export interface ProposalItem {
  id: number;
  kind: string;
  action: ItemAction;
  target_id: number | null;
  status: ItemStatus;
  payload: Record<string, unknown>;
  confidence: number | null;
  /** Shown VERBATIM in the review UI — why the importer believes this draft.
   *  After a failed apply carries `apply_error`. */
  evidence: Record<string, unknown> | null;
  /** False for reserved kinds — accept is refused, apply has no materializer. */
  appliable: boolean;
  depends_on: number[];
  updated_ts: string | null;
  /** Design items only: the document compiled on the fly (§7 trust device —
   *  pre-apply there is no live notification to test-render). */
  compiled_preview?: string;
  compiled_preview_error?: string;
}

export interface QuestionOption {
  label: string;
  /** The plan delta this option applies verbatim (§12.5: the option IS the
   *  decision — no second inference pass). */
  delta?: Record<string, unknown>;
  /** Exactly one option per question carries this — the UI's preselect. */
  recommended?: boolean;
  [key: string]: unknown;
}

export interface ProposalQuestion {
  id: number;
  /** Item this question gates; null = proposal-level. */
  item: number | null;
  prompt: string;
  kind: string;
  blocking: boolean;
  options: QuestionOption[] | null;
  required_permission: string;
  /** Whether the CURRENT user may answer (required_permission holder). */
  can_answer: boolean;
  allow_free_text: boolean;
  allow_discussion: boolean;
  answer: Record<string, unknown> | null;
  answered_by: UserBrief | null;
  answered_ts: string | null;
  /** Set when a discussion round replaced this question — act on the successor. */
  superseded_by: number | null;
  created_ts: string | null;
}

export interface ProposalMaterialBrief {
  id: number;
  kind: string;
  title: string;
  url: string;
}

export interface ProposalDetail extends ProposalListItem {
  items: ProposalItem[];
  questions: ProposalQuestion[];
  materials: ProposalMaterialBrief[];
  /** Ids of the unanswered, un-superseded blocking questions (the apply gate). */
  open_blocking_questions: number[];
  /** The latest apply task for this proposal — the apply receipt lives in
   *  its archived result (created records + edit links). */
  apply_task_id: string | null;
}

export interface StartedTask {
  task_id: string;
  state: number;
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  /** One id or a comma list of ids (D1). */
  tour_operator_id?: TenantIdsParam;
  status?: ProposalStatus;
  origin?: ProposalOrigin;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.tour_operator_id !== undefined) {
    usp.set('tour_operator_id', String(params.tour_operator_id));
  }
  if (params.status) usp.set('status', params.status);
  if (params.origin) usp.set('origin', params.origin);
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(params: ListParams = {}): Promise<PaginatedResponse<ProposalListItem>> {
  return apiGet<PaginatedResponse<ProposalListItem>>(`/composition_proposals/${toQuery(params)}`);
}

export function retrieve(id: number): Promise<ProposalDetail> {
  return apiGet<ProposalDetail>(`/composition_proposals/${id}/`);
}

/** Expand a Figma SECTION/CANVAS link into template frames with proposed
 *  events — fills `material.figma.frames` (the event-picker seed). */
export function discover(body: {
  tour_operator_id: number;
  material_id: number;
}): Promise<StartedTask> {
  return apiPost<StartedTask>('/composition_proposals/discover/', body);
}

export interface IngestFrameSpec {
  /** Figma frame node id. */
  node_id: string;
  event_code: string;
  notification_name?: string;
  email_subject?: string;
}

/** Single form ({event_code, ...}) or batch form ({frames: [...]}) — a batch
 *  is ONE task run and ONE proposal: a notification per design with shared
 *  block/tag/theme proposals. Batch is figma-only, at most 12 designs. */
export function startIngest(body: {
  tour_operator_id: number;
  material_id: number;
  event_code?: string;
  notification_name?: string;
  email_subject?: string;
  node_id?: string;
  frames?: IngestFrameSpec[];
}): Promise<StartedTask> {
  return apiPost<StartedTask>('/composition_proposals/start_ingest/', body);
}

/** 409 carries {detail, question_ids?|task_id?} — open gates or an in-flight task. */
export function startApply(id: number, body: { reason?: string } = {}): Promise<StartedTask> {
  return apiPost<StartedTask>(`/composition_proposals/${id}/start_apply/`, body);
}

export interface ReturnedToReview extends ProposalListItem {
  /** Items reset from `failed` to `proposed`. */
  items_reset: number[];
}

/** A failed (rolled-back) apply back to review. 409 `{detail}` unless the
 *  proposal is failed, or when it was partially applied before the
 *  all-or-nothing engine (re-applying would create rows twice). */
export function returnToReview(id: number): Promise<ReturnedToReview> {
  return apiPost<ReturnedToReview>(`/composition_proposals/${id}/return_to_review/`, {});
}

/** Accept / reject / edit one item: `{status}` and/or `{payload}`. */
export function updateItem(
  proposalId: number,
  itemId: number,
  body: { status?: 'proposed' | 'accepted' | 'rejected'; payload?: Record<string, unknown> },
): Promise<ProposalItem> {
  return apiPost<ProposalItem>(`/composition_proposals/${proposalId}/items/${itemId}/`, body);
}

export function answerQuestion(
  proposalId: number,
  questionId: number,
  body: { option?: number; free_text?: string },
): Promise<ProposalQuestion> {
  return apiPost<ProposalQuestion>(
    `/composition_proposals/${proposalId}/questions/${questionId}/answer/`,
    body,
  );
}

/** Returns the SUCCESSOR question (the original is superseded, never rewritten). */
export function discussQuestion(
  proposalId: number,
  questionId: number,
  body: { text: string },
): Promise<ProposalQuestion> {
  return apiPost<ProposalQuestion>(
    `/composition_proposals/${proposalId}/questions/${questionId}/discuss/`,
    body,
  );
}
